/**
 * Разбор ссылок на канал и на статью (волна 2).
 *
 * <p>Ссылка приходит теми же тремя путями, что и ссылка на профиль, и все три ведут в одно место:
 * <ul>
 *   <li>`svyaz://c/<адрес>` и `svyaz://c/<адрес>/p/<адрес статьи>` — диплинк с публичной
 *       страницы в установленное приложение;
 *   <li>`https://vrmsgr.pro/c/<адрес>[/p/<адрес статьи>]` — сама страница; на устройстве с
 *       приложением её открывает система (universal / app link), в браузере это серверный HTML;
 *   <li>`https://vrmsgr.pro/?c=<адрес>` — веб-клиент: путь `/c/` занят страницей, поэтому в SPA
 *       адрес уходит параметром.
 * </ul>
 *
 * <p>До этого модуля таких ссылок не понимал никто: `handleFromProfileLink` знал только `/u/`, а
 * тест на него прямо закреплял, что `https://vrmsgr.pro/c/channel` — не наша ссылка. Кнопка
 * «Поделиться» раздавала адреса, которые не открывались ни страницей, ни приложением.
 *
 * <p>Ни React, ни состояния: правило «что считать ссылкой на канал» проверяется тестом. Иначе
 * убедиться, что чужая ссылка не открывает чужой канал, можно было бы только на устройстве.
 */

import { isValidPublicHandle, stripHandlePrefix } from './publicHandle';

export type ChannelLinkTarget = {
  /** Адрес канала в хранимой форме. */
  handle: string;
  /** Адрес статьи, если ссылка вела на неё, иначе `null` — тогда открывается сам канал. */
  postSlug: string | null;
};

/**
 * Адрес статьи: тот же алфавит, что у `SlugUtils` на сервере, плюс потолок длины колонки.
 *
 * <p>Проверка нужна не ради красоты: адрес уходит в путь запроса к публичному API, и пускать
 * туда что угодно из чужой ссылки не стоит.
 */
const POST_SLUG = /^[a-z0-9](?:[a-z0-9-]{0,198}[a-z0-9])?$/;

function normalizeChannelHandle(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const h = stripHandlePrefix(raw).toLowerCase();
  return isValidPublicHandle(h) ? h : null;
}

function normalizePostSlug(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  return POST_SLUG.test(s) ? s : null;
}

/** `c/<адрес>` или `c/<адрес>/p/<адрес статьи>` из уже отрезанного пути. */
function fromPathTail(tail: string): ChannelLinkTarget | null {
  const m = tail.match(/^c\/([^/?#]+)(?:\/p\/([^/?#]+))?\/?$/i);
  if (!m) return null;
  const handle = normalizeChannelHandle(decodeURIComponent(m[1]));
  if (!handle) return null;
  // Негодный адрес статьи не отменяет ссылку целиком: открыть канал — разумный запасной путь,
  // и он лучше, чем «ссылка не наша» и экран входа.
  const postSlug = m[2] ? normalizePostSlug(decodeURIComponent(m[2])) : null;
  return { handle, postSlug };
}

/**
 * Канал и статья из ссылки, либо `null`, если это не наша ссылка на канал.
 *
 * <p>Хост не проверяется намеренно — по той же причине, что и у ссылки на профиль: домен сайта
 * настраивается, на стенде он другой, а вреда нет — открытие всё равно упирается в существующий
 * канал с этим адресом.
 */
export function channelFromLink(href: string | null | undefined): ChannelLinkTarget | null {
  if (!href) return null;
  const raw = href.trim();
  if (raw.length === 0) return null;

  // `svyaz://c/blog` — URL с нестандартной схемой: у него пустой host в одних движках и
  // host === 'c' в других, поэтому разбираем строкой, а не через URL.
  const scheme = raw.match(/^([a-z][a-z0-9+.-]*):\/\/(.*)$/i);
  if (scheme && !/^https?$/i.test(scheme[1])) {
    return fromPathTail(scheme[2].replace(/^\/+/, ''));
  }

  try {
    const url = new URL(raw);
    const fromQuery = normalizeChannelHandle(url.searchParams.get('c'));
    if (fromQuery) {
      return { handle: fromQuery, postSlug: normalizePostSlug(url.searchParams.get('p')) };
    }
    return fromPathTail(url.pathname.replace(/^\/+/, ''));
  } catch {
    return null;
  }
}
